/// <reference path="./globals.d.ts" />
import { useEffect, useState } from 'react';
import { Box, Typography, Alert, Paper, Button } from '@mui/material';
import { useAuth } from './AuthProvider';
import { getToken } from './AuthProvider';
import { useCadConfig } from '../cad/cad-config';
import { useSnackbar } from '../hooks/use-snackbar';

interface DebugInfo {
  provider: string;
  isAuthenticated: boolean;
  isPending: boolean;
  user: { email?: string; name?: string; sub?: string } | null;
  hasToken: boolean;
  url: string;
  urlParams: Record<string, string>;
  windowVars: {
    oktaClientId: string | undefined;
    oktaIssuer: string | undefined;
    oktaScopes: string | undefined;
    baseUrl: string | undefined;
    hasOktaToken: boolean;
    hasRefreshToken: boolean;
  };
  envVars: {
    VITE_OKTA_CLIENT_ID: string | undefined;
    VITE_OKTA_ISSUER: string | undefined;
    VITE_OKTA_DOMAIN: string | undefined;
    VITE_OKTA_AUTH_SERVER_ID: string | undefined;
    VITE_OKTA_SCOPES: string | undefined;
  };
  cadConfig: {
    enabled: boolean;
    loginUrl: string;
    clientId: string;
    scopes: string;
  };
  error?: string;
}

export interface AuthDebugPanelProps {
  /** Optional title shown at the top of the panel. */
  title?: string;
}

/**
 * Provider-agnostic auth debug panel. Surfaces the unified `useAuth()` state
 * plus the relevant Okta + CAD configuration sourced from `window.*` and
 * `import.meta.env.*`. Both Okta and CAD details are always displayed so the
 * panel is useful for diagnosing either provider.
 */
export function AuthDebugPanel({ title }: AuthDebugPanelProps = {}) {
  const auth = useAuth();
  const cadConfig = useCadConfig();
  const { addSnackbar } = useSnackbar();
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);

  const handleLogin = async () => {
    try {
      await auth.login();
    } catch (error) {
      addSnackbar(
        'Error initiating login. Please try again.',
        'error',
        error instanceof Error ? error.message : String(error),
        true,
      );
    }
  };

  const handleClearTokens = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      addSnackbar('Tokens cleared. Please sign in again.', 'info');
      window.location.href = '/';
    } catch (error) {
      addSnackbar(
        'Error clearing tokens.',
        'error',
        error instanceof Error ? error.message : String(error),
        true,
      );
    }
  };

  const handleForceReauth = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      await auth.login();
    } catch (error) {
      addSnackbar(
        'Error forcing re-authentication.',
        'error',
        error instanceof Error ? error.message : String(error),
        true,
      );
    }
  };

  useEffect(() => {
    const gather = async () => {
      try {
        const token = await getToken();
        // `import.meta.env` is augmented with VITE_OKTA_* keys by the
        // `globals.d.ts` reference at the top of this file. (CAD config
        // does not use env vars — see `cadConfig` block below.)
        const env = import.meta.env;
        setDebugInfo({
          provider: auth.provider,
          isAuthenticated: auth.isAuthenticated,
          isPending: auth.isPending,
          user: auth.user,
          hasToken: Boolean(token),
          url: window.location.href,
          urlParams: Object.fromEntries(new URLSearchParams(window.location.search)),
          windowVars: {
            oktaClientId: window.oktaClientId,
            oktaIssuer: window.oktaIssuer,
            oktaScopes: window.oktaScopes,
            baseUrl: window.baseUrl,
            hasOktaToken: Boolean(window.oktaToken),
            hasRefreshToken: Boolean(window.refreshToken),
          },
          envVars: {
            VITE_OKTA_CLIENT_ID: env.VITE_OKTA_CLIENT_ID,
            VITE_OKTA_ISSUER: env.VITE_OKTA_ISSUER,
            VITE_OKTA_DOMAIN: env.VITE_OKTA_DOMAIN,
            VITE_OKTA_AUTH_SERVER_ID: env.VITE_OKTA_AUTH_SERVER_ID,
            VITE_OKTA_SCOPES: env.VITE_OKTA_SCOPES,
          },
          cadConfig: {
            enabled: cadConfig.enabled,
            loginUrl: cadConfig.loginUrl,
            clientId: cadConfig.clientId,
            scopes: cadConfig.scopes,
          },
        });
      } catch (error) {
        addSnackbar(
          'Error gathering debug info.',
          'error',
          error instanceof Error ? error.message : String(error),
          true,
        );
        setDebugInfo((prev) =>
          prev
            ? { ...prev, error: error instanceof Error ? error.message : String(error) }
            : null,
        );
      }
    };

    gather();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addSnackbar is stable from Zustand
  }, [auth.isAuthenticated, auth.isPending, auth.provider, cadConfig.enabled]);

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        {title ?? 'Authentication Debug Info'}
      </Typography>

      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          Test Authentication ({auth.provider.toUpperCase()})
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleLogin}
            disabled={auth.isAuthenticated}
          >
            {auth.isAuthenticated ? 'Already Authenticated' : 'Start Login Flow'}
          </Button>
          <Button variant="contained" color="warning" onClick={handleClearTokens}>
            Clear Tokens & Logout
          </Button>
          <Button variant="contained" color="secondary" onClick={handleForceReauth}>
            Force Re-Authentication
          </Button>
        </Box>
        {auth.isAuthenticated && (
          <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
            User is authenticated. Navigate back to the app to continue.
          </Typography>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Auth State</Typography>
        <pre style={{ fontSize: '12px', overflow: 'auto' }}>
          {JSON.stringify(
            {
              provider: debugInfo?.provider,
              isAuthenticated: debugInfo?.isAuthenticated,
              isPending: debugInfo?.isPending,
              user: debugInfo?.user,
              hasToken: debugInfo?.hasToken,
            },
            null,
            2,
          )}
        </pre>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">CAD Configuration</Typography>
        <pre style={{ fontSize: '12px', overflow: 'auto' }}>
          {JSON.stringify(debugInfo?.cadConfig, null, 2)}
        </pre>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">URL Information</Typography>
        <Typography variant="body2">URL: {debugInfo?.url}</Typography>
        <pre style={{ fontSize: '12px', overflow: 'auto' }}>
          URL Params: {JSON.stringify(debugInfo?.urlParams, null, 2)}
        </pre>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Environment Variables</Typography>
        <pre style={{ fontSize: '12px', overflow: 'auto' }}>
          Window: {JSON.stringify(debugInfo?.windowVars, null, 2)}
        </pre>
        <pre style={{ fontSize: '12px', overflow: 'auto' }}>
          Vite Env: {JSON.stringify(debugInfo?.envVars, null, 2)}
        </pre>
      </Paper>

      {debugInfo?.error && <Alert severity="error">Error: {debugInfo.error}</Alert>}
    </Box>
  );
}
